import React from "react";
import { fmtSignedMoney, fmtPct, driverCause } from "../format";
import { describeUsageType } from "../../cost-analyzer/analyzerDimensions";

export default function DriverDecompositionTable({ drivers = [], highlightKey }) {
  // Hide drivers with no material change (€0 / 0%) — they're noise on a variance
  // view. Keep a count so the total is still honest.
  const shown = (drivers || []).filter((d) => Math.abs(d.deltaCost || 0) >= 0.005);
  const hidden = (drivers?.length || 0) - shown.length;
  if (!shown.length) {
    return (
      <div className="text-text-3">
        No drivers with a material change{hidden > 0 ? ` (${hidden} unchanged)` : ""}.
      </div>
    );
  }
  return (
    <div className="rounded-card border border-card-bd bg-card">
      <div className="flex items-center gap-4 px-5 pt-4 text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3">
        <div className="flex-1">Driver</div>
        <div className="w-[120px]">Cause</div>
        <div className="w-[160px] normal-case">Usage / price</div>
        <div className="w-[90px] text-right">Δ</div>
        <div className="w-[52px] text-right">share</div>
      </div>
      {shown.map((d) => {
        const pv = d.priceVolume;
        const pe = Math.abs(pv?.priceEffect || 0);
        const ve = Math.abs(pv?.volumeEffect || 0);
        const isHighlight = highlightKey != null && String(d.key) === String(highlightKey);
        // A usage-type driver's name is itself a raw billing unit → describe it.
        const rawName = d.label || d.key;
        const isUsageType = d.dimension === "usage_type";
        const name = isUsageType ? describeUsageType(rawName) : rawName;
        // Drop the redundant "usage_type" dimension prefix — the described name already
        // conveys it; keep the dimension prefix for other lenses (e.g. "service").
        const metaText = [isUsageType ? "" : d.dimension, pv?.unit ? describeUsageType(pv.unit) : ""].filter(Boolean).join(" · ");
        return (
          <div key={`${d.dimension}:${d.key}`} data-driver-key={d.key} className={`flex items-center gap-4 border-b border-card-bd px-5 py-3 last:border-b-0 ${isHighlight ? "bg-primary-soft" : ""}`}>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-medium text-text">{name}</div>
              <div className="text-[12px] text-text-3">
                {isHighlight && <span className="font-semibold text-primary">Biggest mover{metaText ? " · " : ""}</span>}
                {metaText}
              </div>
            </div>
            <div className="w-[120px] text-[12px] text-text-2">{driverCause(d)}</div>
            <div className="w-[160px] text-[12px] tabular-nums text-text-3">
              {pv && (pe + ve > 0)
                ? <span><span className="text-text-2">Usage {fmtSignedMoney(pv.volumeEffect)}</span> · Price {fmtSignedMoney(pv.priceEffect)}</span>
                : "—"}
            </div>
            <div className="w-[90px] text-right text-[14px] font-semibold tabular-nums text-text">{fmtSignedMoney(d.deltaCost)}</div>
            <div className="w-[52px] text-right text-[13px] text-text-3 tabular-nums">{fmtPct(d.sharePct)}</div>
          </div>
        );
      })}
      {hidden > 0 && (
        <div className="px-5 py-2 text-[12px] text-text-3">
          +{hidden} driver{hidden === 1 ? "" : "s"} with no material change hidden
        </div>
      )}
    </div>
  );
}
